import React from 'react';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutlined';
import { Box, Button, Typography } from '@mui/material';
import { useFieldArray, useFormContext } from 'react-hook-form';
import {
  CURRENCY_EXCHANGES_ADD_BUTTON_LABEL,
  CURRENCY_EXCHANGES_SECTION_TITLE,
  CURRENCY_EXCHANGES_TABLE_ARIA_LABEL,
  CURRENCY_EXCHANGES_TABLE_COLUMN_WIDTHS,
  CURRENCY_EXCHANGES_TABLE_HEADERS,
} from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/financialCondition/components/currencyExchanges/constants.ts';
import { CurrencyExchangeRow } from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/financialCondition/components/currencyExchanges/CurrencyExchangeRow.tsx';
import { CurrencyExchangesTotals } from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/financialCondition/components/currencyExchanges/CurrencyExchangesTotals.tsx';
import { FinancialConditionTable } from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/financialCondition/components/shared/FinancialConditionTable.tsx';
import type { ICurrencyExchangesSectionProps } from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/financialCondition/components/currencyExchanges/types.ts';
import { useDocumentGenerationOptions } from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/financialCondition/hooks/useDocumentGenerationOptions.ts';
import type { TFinancialConditionFormValues } from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/financialCondition/types.ts';
import {
  createCurrencyExchange,
  getCurrencyExchangeTotals,
} from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/financialCondition/utils.ts';
import { documentGenerationFormStyles } from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/styles.ts';

export const CurrencyExchangesSection: React.FC<ICurrencyExchangesSectionProps> = ({
  disabled,
}) => {
  const { control, watch } = useFormContext<TFinancialConditionFormValues>();
  const borrowerType = watch('borrowerType')?.value;
  const currencyExchanges = watch('currencyExchanges');
  const { fields, append, remove } = useFieldArray({ control, name: 'currencyExchanges' });
  const { bankOptions, currencyOptions, isBanksLoading, isCurrenciesLoading } =
    useDocumentGenerationOptions();
  const totals = getCurrencyExchangeTotals(currencyExchanges);

  if (!borrowerType) {
    return null;
  }

  return (
    <Box sx={documentGenerationFormStyles.section}>
      <Typography component="h3" sx={documentGenerationFormStyles.sectionTitle}>
        {CURRENCY_EXCHANGES_SECTION_TITLE}
      </Typography>
      <FinancialConditionTable
        ariaLabel={CURRENCY_EXCHANGES_TABLE_ARIA_LABEL}
        headers={[...CURRENCY_EXCHANGES_TABLE_HEADERS]}
        showActions={fields.length > 1}
        columnWidths={[...CURRENCY_EXCHANGES_TABLE_COLUMN_WIDTHS]}
      >
        {fields.map((field, index) => (
          <CurrencyExchangeRow
            key={field.id}
            index={index}
            canRemove={fields.length > 1}
            disabled={disabled}
            bankOptions={bankOptions}
            currencyOptions={currencyOptions}
            isBanksLoading={isBanksLoading}
            isCurrenciesLoading={isCurrenciesLoading}
            onRemove={() => remove(index)}
          />
        ))}
      </FinancialConditionTable>
      <Button
        variant="outlined"
        startIcon={<AddCircleOutlineIcon />}
        onClick={() => append(createCurrencyExchange())}
        disabled={disabled}
        sx={documentGenerationFormStyles.addButton}
      >
        {CURRENCY_EXCHANGES_ADD_BUTTON_LABEL}
      </Button>
      <CurrencyExchangesTotals totals={totals} />
    </Box>
  );
};
